// Viso platform data — reworded product content aligned with theme.

export const VISO_PLATFORMS = {
  'viso-yard': {
    slug: 'viso-yard',
    name: 'Viso Yard',
    kicker: 'Viso Yard — nine systems, your cameras',
    tagline: 'Container & Terminal Vision',
    hero:
      'Every container, every checkpoint, on the record — from the CCTV you already own.',
    accent: 'from-cyan-400 to-sky-500',
    poster: 'https://images.pexels.com/photos/28185415/pexels-photo-28185415.jpeg',
    products: [
      {
        id: 'container-vision',
        num: '01',
        name: 'Container Vision',
        kicker: 'Patented damage detection',
        headline: 'Every dent, rust & crack — documented automatically.',
        body:
          'Capture every side from the CCTV you already have. Detect and segment every defect — type, dimension, location and area in mm² — then diff any two checkpoints (gate in, crane on/off, gate out). Full report in under a minute.',
        bullets: [
          'Capture from existing CCTV',
          'Type · Dimension · Location · Area (mm²)',
          'Checkpoint-to-checkpoint diff',
          'Report in under a minute',
        ],
        stats: [
          { v: '48 s', l: 'turnaround (was 48 hours)' },
          { v: 'ISO 6346', l: 'ID standard supported' },
        ],
        footer:
          'Chosen by industrial leaders like Adani, DP World, Hind Terminals. Damage above your threshold emails the concerned authority automatically. Decodes low-light, motion blur and partial occlusion where generic OCR and leading vision models fail.',
      },
      {
        id: 'tank-vision',
        num: '02',
        name: 'Tank Vision',
        kicker: 'ISO tank health',
        headline: 'Tank health detection — from the cameras you already have.',
        body:
          'Frame-type-aware capture for ISO tank containers (T11 and beyond). Detect corrosion, valve damage and structural anomalies with annotated evidence attached to every event.',
        bullets: [
          'ISO tank frame recognition',
          'Corrosion & valve inspection',
          'Six-face evidence pack',
          'Threshold-based alerts',
        ],
      },
      {
        id: 'gate-vision',
        num: '03',
        name: 'Gate Vision',
        kicker: '~100% ID read accuracy',
        headline: 'Read every ID on the move — night, rain, fog, dust.',
        body:
          'Read container, ISO, trailer and wagon IDs on moving trucks in the harshest conditions. Log the verified event — ID, seal check and timestamp — in a single pass without stop-and-shoot.',
        bullets: [
          'On-the-move recognition',
          'Container · ISO · Trailer · Wagon',
          'Seal check + timestamp per event',
          'JSON via API in real time',
        ],
        stats: [
          { v: '400,000', l: 'daily reads · 25+ yards' },
          { v: '~100%', l: 'ID read accuracy' },
        ],
        footer:
          'The engine behind every gate on the platform. Beats leading vision APIs in our benchmarks — at lower cost. Exception events emailed automatically.',
      },
      {
        id: 'yard-vision',
        num: '04',
        name: 'Yard Vision',
        kicker: 'Live location',
        headline: 'One survey. Then the yard runs on a live twin.',
        body:
          'A one-time survey maps your yard into a live twin. Every inbound gets a recommended slot; every move is written into the twin automatically. Ask for a container — get the bay.',
        bullets: [
          'One-time survey',
          'Placement planning per inbound',
          'Live locator on every move',
          'Bay-level precision',
        ],
        stats: [{ v: '80%', l: 'of asset-tracking time saved' }],
      },
      {
        id: 'crane-vision',
        num: '05',
        name: 'Crane Vision',
        kicker: 'Chain of custody',
        headline: 'Every face, every lift, time-stamped.',
        body:
          'Multi-camera capture per lift with sharpest-frame selection — so crane vibration never becomes an inspection error. High-severity findings alert a surveyor for immediate review.',
        bullets: [
          'Multi-camera capture per lift',
          'Sharpest-frame selection',
          'Vibration-compensated · motion-blur-corrected',
          'Severity heatmap with surveyor alerts',
        ],
        footer:
          'A definitive record at the exact moment of discharge or load — chain of custody from vessel to yard to gate.',
      },
      {
        id: 'cargo-vision',
        num: '06',
        name: 'Cargo Vision',
        kicker: 'Count with proof',
        headline: 'Every case counted — with video proof attached.',
        body:
          'Detect every item on video during stuffing / destuffing. Inspect for damage in the same pass. Alert the command centre in real time — even offline.',
        bullets: [
          'Cartons · Gunny bags · Jumbo bags',
          'Pallets · Drums · Barrels',
          'Damage inspection same pass',
          'Edge compute — zero-internet operation',
        ],
        stats: [{ v: '60%', l: 'reduction in inventory shrinkage' }],
      },
      {
        id: 'document-vision',
        num: '07',
        name: 'Document Vision',
        kicker: 'Key-value extraction',
        headline: 'Bill of Lading in. Structured data out.',
        body:
          'Reads Bills of Lading and industrial documents where generic OCR fails in our benchmarks. Key-value + table extraction pushed straight to your system.',
        bullets: [
          'Key-value + table extraction',
          'Bill of Lading · Invoice · Manifest',
          'Auto-processing pipeline',
          'Pushed to your system via API',
        ],
      },
      {
        id: 'work-vision',
        num: '08',
        name: 'Work Vision',
        kicker: 'Zero-interruption presence',
        headline: 'Nobody stops. Nobody even notices.',
        body:
          'No cards to tap, no scanners to use, no habit to change. Standard CCTV captures the work as it already happens — and turns it into an exact attendance record.',
        bullets: [
          'Presence read from existing streams',
          'Worker · zone · duration resolved',
          'Entry & exit to the second',
          'Build · gait · posture — face not required',
        ],
        stats: [{ v: '4 days', l: 'from install to verified presence' }],
        footer:
          'A scanner is a checkpoint, and a checkpoint is a queue. The cameras don\u2019t need anyone to stop.',
      },
      {
        id: 'secure-vision',
        num: '09',
        name: 'Secure Vision',
        kicker: 'Alerts and logs · does not deter',
        headline: 'An alarm that cries wolf is worse than no alarm.',
        body:
          'A guard cannot watch forty feeds. The platform watches all of them — and already knows the difference between a real event and a flapping tarp, because it reads through night, rain, fog, dust and motion blur.',
        bullets: [
          'Continuous triage on every feed',
          'Nuisance filtered — tarp · shadow · rain',
          'Webhook alerts to your systems',
          'Tamper-evident case log with clip',
        ],
        footer:
          'It alerts and logs. It does not deter — your alarm, your lights, your PA. We don\u2019t sell you a strobe.',
      },
    ],
    cta: {
      title: 'Join 25+ sites running 400,000 reads a day.',
      sub: "Bring a gate feed. We'll read it live.",
    },
  },

  'viso-warehouse': {
    slug: 'viso-warehouse',
    name: 'Viso Warehouse',
    kicker: 'Our platform — your cameras · inbound to outbound',
    tagline: 'Warehouse & DC Vision',
    hero:
      'Every case counted, every pallet dimensioned, every order proven \u2014 on the cameras already covering your floor.',
    accent: 'from-cyan-400 to-teal-400',
    poster: 'https://images.pexels.com/photos/34207359/pexels-photo-34207359.jpeg',
    products: [
      {
        id: 'cargo-vision',
        num: '01',
        name: 'Cargo Vision',
        kicker: 'Count with proof',
        headline: 'Every case counted \u2014 with video proof attached.',
        body:
          'Automatic, accurate count with video proof per session. Detect every item on video during stuffing / destuffing, inspect for damage in the same pass, and alert the command centre in real time \u2014 even offline.',
        bullets: [
          'Cartons · Gunny bags · Jumbo bags',
          'Pallets · Drums · Barrels',
          'Damage flagged in the same pass',
          'Full detection and alerting on-board',
        ],
        stats: [{ v: '60%', l: 'reduction in inventory shrinkage' }],
      },
      {
        id: 'audit-vision',
        num: '02',
        name: 'Audit Vision',
        kicker: 'ID-linked video proof',
        headline: 'The dispute arrives. The clip already exists.',
        body:
          'Every order packed, verified or returned is recorded and bound to its ID \u2014 automatically, because the system can see the work happening. Search the ID, get the moment.',
        bullets: [
          'Detect, don\u2019t trigger — no barcode, no button',
          'Bind to the ID in a tamper-evident log',
          'Search \u2192 clip \u2192 secure link in seconds',
          'The ID is the index, not the timeline',
        ],
      },
      {
        id: 'dimension-vision',
        num: '03',
        name: 'Dimension Vision',
        kicker: 'Volumetric capture',
        headline: 'The tape measure retires.',
        body:
          'Cartons, sacks, drums and pallets \u2014 including irregular shapes a tape measure was never going to agree on \u2014 captured as volumetric data with an annotated image attached.',
        bullets: [
          'L · W · H captured from camera view',
          'Annotated evidence attached',
          'Irregulars included — sacks, drums, soft packs',
          'Pushed to WMS / ERP',
        ],
      },
      {
        id: 'document-vision',
        num: '04',
        name: 'Document Vision',
        kicker: 'Key-value extraction',
        headline: 'Bill of Lading in. Structured data out.',
        body:
          'Reads Bills of Lading and industrial documents where generic OCR fails. Key-value plus table extraction pushed to your system automatically.',
        bullets: ['Auto-processing pipeline', 'Key-value + table extraction', 'Signature detection', 'API to your system'],
      },
      {
        id: 'work-vision',
        num: '05',
        name: 'Work Vision',
        kicker: 'Zero-interruption presence',
        headline: 'Nobody stops. Nobody even notices.',
        body:
          'No cards to tap, no scanners to use, no habit to change. Standard CCTV captures the work as it already happens \u2014 and turns it into an exact attendance record.',
        bullets: [
          'Presence read from existing streams',
          'Worker · zone · duration resolved',
          'Entry & exit to the second',
          'Face not required',
        ],
        stats: [{ v: '4 days', l: 'from install to verified presence' }],
      },
      {
        id: 'secure-vision',
        num: '06',
        name: 'Secure Vision',
        kicker: 'Alerts and logs',
        headline: 'An alarm that cries wolf is worse than no alarm.',
        body:
          'The platform watches every feed and already knows the difference between a real event and a flapping tarp \u2014 because it reads through night, rain, fog, dust and motion blur.',
        bullets: [
          'Continuous triage on every feed',
          'Nuisance filtered automatically',
          'Webhook alert with clip attached',
          'Tamper-evident case logged',
        ],
      },
    ],
    cta: {
      title: 'Every case counted. Every order proven.',
      sub: 'Bring an inbound feed. We\u2019ll show you what it catches.',
    },
  },

  'viso-factory': {
    slug: 'viso-factory',
    name: 'Viso Factory',
    kicker: 'Our platform — your cameras · on the line',
    tagline: 'Production & Line Vision',
    hero:
      'Production and process, watched continuously \u2014 from the cameras already on your line.',
    accent: 'from-cyan-400 to-emerald-400',
    poster: 'https://images.pexels.com/photos/34207359/pexels-photo-34207359.jpeg',
    products: [
      {
        id: 'production-vision',
        num: '01',
        name: 'Production Vision',
        kicker: 'Count · Identify · Inspect',
        headline: 'Every unit counted, identified and inspected as it passes.',
        body:
          'No manual tally, no separate inspection station. Count, SKU and condition are read in the same pass, on the same camera \u2014 the moment the unit leaves the line.',
        bullets: [
          'COUNT this shift',
          'SKU ID read on the fly',
          'INSPECT — defects flagged in-line',
          'TRACE unit-level history',
        ],
      },
      {
        id: 'audit-vision',
        num: '02',
        name: 'Audit Vision',
        kicker: 'ID-linked video proof',
        headline: 'The dispute arrives. The clip already exists.',
        body:
          'Every order packed, verified or returned is recorded and bound to its ID \u2014 automatically. Search the ID, get the moment.',
        bullets: [
          'Detection-triggered, not scan-triggered',
          'Bound to the ID',
          'Search returns the clip',
          'Tamper-evident log',
        ],
      },
      {
        id: 'dimension-vision',
        num: '03',
        name: 'Dimension Vision',
        kicker: 'Volumetric capture',
        headline: 'The tape measure retires.',
        body:
          'Cartons, sacks, drums, pallets and irregulars \u2014 captured as volumetric data with annotated evidence attached.',
        bullets: [
          'Captured, not estimated',
          'Annotated image rides with the number',
          'Irregulars included',
          'Pushed to WMS / ERP',
        ],
      },
      {
        id: 'work-vision',
        num: '04',
        name: 'Work Vision',
        kicker: 'Zero-interruption presence',
        headline: 'Nobody stops. Nobody even notices.',
        body:
          'Presence read from existing camera streams. No new hardware, no checkpoint, no queue.',
        bullets: ['No cards, no scanners', 'Worker · zone · duration', 'Entry & exit to the second', 'Existing CCTV'],
        stats: [{ v: '4 days', l: 'from install to verified presence' }],
      },
      {
        id: 'secure-vision',
        num: '05',
        name: 'Secure Vision',
        kicker: 'Alerts and logs',
        headline: 'An alarm that cries wolf is worse than no alarm.',
        body:
          'Every feed watched continuously. Real events triaged from nuisance. Clip and case delivered to your systems by webhook.',
        bullets: ['Continuous triage', 'Nuisance filtered', 'Webhook alert with clip', 'Tamper-evident case log'],
      },
    ],
    cta: {
      title: 'Every unit counted. Every defect caught.',
      sub: 'Point a line camera at us. We\u2019ll show you what it catches.',
    },
  },

  'viso-data': {
    slug: 'viso-data',
    name: 'Viso Data',
    kicker: 'Foundation AI · Compression · Trace · Detect',
    tagline: 'Foundation AI for Video Operations',
    hero:
      'The footage layer beneath every checkpoint. Compress it, ask it questions, and let it flag what matters.',
    accent: 'from-cyan-400 to-indigo-400',
    poster: 'https://images.unsplash.com/photo-1767112601499-1b78cfb31a62',
    products: [
      {
        id: 'compression-ai',
        num: '01',
        name: 'Compression AI',
        kicker: 'Same footage, fewer bytes',
        headline: 'The footage you need is the footage you deleted.',
        body:
          'Same footage. Fewer bytes. Analytics unaffected. Deployed on-premise in front of your existing storage \u2014 the platform continues to run on the compressed stream.',
        bullets: [
          'INGEST — RTSP · ONVIF',
          'CODEC preserved — H.264 · H.265 · MJPEG',
          'On-premise deployment',
          'Analytics runs on the compressed stream',
        ],
      },
      {
        id: 'trace-ai',
        num: '02',
        name: 'Trace AI',
        kicker: 'Ask the archive',
        headline: 'Ask a question. Get the timeline that answers it.',
        body:
          'Natural-language query across every camera and every hour \u2014 it reconstructs the timeline and hands you the clips that prove it. The footage exists; finding the exact seconds is the whole job.',
        bullets: [
          '\u201cWhich trucks were at Bay 4 when the seal broke?\u201d',
          '\u201cShow me every container handled between gate-in and crane-off.\u201d',
          '\u201cHow long did Dock 2 sit idle last Tuesday?\u201d',
          'Answers with the exact seconds, not the timeline.',
        ],
      },
      {
        id: 'detect-ai',
        num: '03',
        name: 'Detect AI',
        kicker: 'Continuous triage · every feed',
        headline: 'A guard cannot watch forty feeds. The platform watches all of them.',
        body:
          'It already knows the difference between a real event and a flapping tarp \u2014 because it reads through night, rain, fog, dust and motion blur.',
        bullets: [
          'DETECTED — continuous, every feed',
          'TRIAGED — real event vs. nuisance',
          'ALERTED — to your systems, over webhook',
          'CLIP + CASE — attached and logged, tamper-evident',
        ],
        footer:
          'It alerts and logs. It does not deter \u2014 your alarm, your lights, your PA. We don\u2019t sell you a strobe.',
      },
    ],
    cta: {
      title: 'The footage exists. We find the seconds.',
      sub: 'Point us at your archive. We\u2019ll answer your questions in clips.',
    },
  },
};

export const VISO_PLATFORM_LIST = Object.values(VISO_PLATFORMS);
